import { useDeferredValue, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Input } from '@/components/ui/input'
import { useFraudAdminEvents } from '@/lib/api/fraud-queries'
import type { FraudAdminEvent } from '@/lib/api/fraud-types'
import { cn } from '@/lib/utils'
import { ModeChip, Spinner } from '../shared'
import { EMPTY, PILL, ROW, TD, TH, fmtDateTime, moneyFormatter, relativeTime } from '../format'

const WINDOWS = [
  { value: '60m', label: '1 hour' },
  { value: '360m', label: '6 hours' },
  { value: '1440m', label: '24 hours' },
] as const

const PHASE_CHIP: Record<FraudAdminEvent['phase'], string> = {
  live: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
  upcoming: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
  ended: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
}

/** Every event's fraud posture in one table, worst first within the window. */
export function AdminEventsTable() {
  const [window, setWindow] = useState<(typeof WINDOWS)[number]['value']>('1440m')
  const [scope, setScope] = useState<'active' | 'all'>('active')
  const [q, setQ] = useState('')
  const deferredQ = useDeferredValue(q)
  const { data, isLoading, error, dataUpdatedAt } = useFraudAdminEvents({ window, scope, q: deferredQ.trim() || undefined })
  const rows = [...(data?.results ?? [])].sort((a, b) =>
    (b.window.refused + b.window.blocked_attempts) - (a.window.refused + a.window.blocked_attempts)
    || b.window.step_ups - a.window.step_ups
    || (a.start_date_time ?? '').localeCompare(b.start_date_time ?? ''))

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Event or organization" className="w-64" />
          <div className="inline-flex rounded-lg border border-neutral-200 dark:border-neutral-800 overflow-hidden">
            {WINDOWS.map((w) => (
              <button
                key={w.value}
                onClick={() => setWindow(w.value)}
                className={cn('px-3 py-1.5 text-sm', window === w.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-neutral-950 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800')}
              >
                {w.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
            <input type="checkbox" checked={scope === 'all'} onChange={(e) => setScope(e.target.checked ? 'all' : 'active')} />
            Include past events
          </label>
        </div>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          {rows.length} event{rows.length === 1 ? '' : 's'}{dataUpdatedAt ? ` · updated ${relativeTime(new Date(dataUpdatedAt).toISOString())}` : ''}
        </p>
      </div>

      <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 overflow-x-auto">
        {error ? (
          <p className="p-6 text-sm text-red-600 dark:text-red-400">Failed to load events: {error.message}</p>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner /></div>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-neutral-500 dark:text-neutral-400">No events match.</p>
        ) : (
          <table className="w-full whitespace-nowrap">
            <thead>
              <tr className="border-b border-neutral-200 dark:border-neutral-800">
                <th className={TH}>Event</th>
                <th className={TH}>Mode</th>
                <th className={TH}>Profile</th>
                <th className={TH}>Card reloads</th>
                <th className={TH}>Step-ups</th>
                <th className={TH}>Refused</th>
                <th className={TH}>Provider declines</th>
                <th className={TH}>Trusted</th>
                <th className={TH}>Blocks</th>
                <th className={TH}>Chargebacks</th>
                <th className={TH} />
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => {
                const money = moneyFormatter(e.currency)
                const hot = e.window.refused + e.window.blocked_attempts > 0
                return (
                  <tr key={e.id} className={cn(ROW, hot && 'bg-amber-50/60 dark:bg-amber-900/10')}>
                    <td className={TD}>
                      <Link to="/events/$eventId/fraud" params={{ eventId: String(e.id) }} search={{}} className="font-medium text-blue-600 dark:text-blue-400 hover:underline">
                        {e.name}
                      </Link>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400">
                        {e.organization?.name ?? EMPTY} · {fmtDateTime(e.start_date_time)}
                        <span className={cn(PILL, 'ml-2', PHASE_CHIP[e.phase])}>{e.phase}</span>
                        {!e.enable_evervault && <span className={cn(PILL, 'ml-1 bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400')}>no Evervault</span>}
                      </div>
                    </td>
                    <td className={TD}>
                      <ModeChip mode={e.policy.mode} />
                      {e.policy.mode_override && <div className="text-xs text-neutral-500 dark:text-neutral-400">set on the event</div>}
                      {e.policy.require_3ds_for_all && <div className="text-xs text-red-600 dark:text-red-400">challenge everything</div>}
                    </td>
                    <td className={cn(TD, 'capitalize')}>{e.policy.profile}</td>
                    <td className={TD}>{e.window.card_reloads}<div className="text-xs text-neutral-500 dark:text-neutral-400">{money(e.window.card_reload_amount)}</div></td>
                    <td className={TD}>{e.window.step_ups}</td>
                    <td className={cn(TD, e.window.refused && 'text-red-600 dark:text-red-400 font-medium')}>
                      {e.window.refused}{e.window.blocked_attempts ? <div className="text-xs">{e.window.blocked_attempts} blocked</div> : null}
                    </td>
                    <td className={cn(TD, e.window.provider_declined >= 5 && 'text-amber-600 dark:text-amber-400')}>{e.window.provider_declined}</td>
                    <td className={TD}>{e.trusted}</td>
                    <td className={cn(TD, e.active_blocks && 'text-red-600 dark:text-red-400')}>{e.active_blocks}</td>
                    <td className={TD}>
                      {e.chargebacks.count}{e.chargebacks.open ? <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">{e.chargebacks.open} open</span> : null}
                    </td>
                    <td className={TD}>
                      <Link to="/events/$eventId/fraud" params={{ eventId: String(e.id) }} search={{ tab: 'policy' }} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                        Policy
                      </Link>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
